import React, { useState, useEffect } from 'react';

const SplashScreen = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    // Rotate the geometric shapes
    const rotateInterval = setInterval(() => {
      setRotation(prev => prev + 1);
    }, 50);

    // Simulate loading progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 3;
      });
    }, 80);

    // Hide splash screen after 3.5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onComplete();
      }, 600);
    }, 3500);

    return () => {
      clearInterval(interval);
      clearInterval(rotateInterval);
      clearTimeout(timer);
    };
  }, [onComplete]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black overflow-hidden">
      {/* Animated geometric background */}
      <div className="absolute inset-0">
        {/* Rotating hexagon */}
        <div 
          className="absolute top-1/2 left-1/2 w-96 h-96 border-2 border-cyan-400/30 transform -translate-x-1/2 -translate-y-1/2"
          style={{ 
            transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
          }}
        />
        
        {/* Inner rotating square */}
        <div 
          className="absolute top-1/2 left-1/2 w-64 h-64 border-2 border-purple-400/40 transform -translate-x-1/2 -translate-y-1/2"
          style={{ 
            transform: `translate(-50%, -50%) rotate(${-rotation * 0.7}deg)`
          }}
        />
        
        {/* Floating triangles */}
        <div className="absolute top-20 left-20 w-16 h-16 border border-pink-400/50 transform rotate-45 animate-pulse" />
        <div className="absolute top-32 right-32 w-12 h-12 border border-blue-400/50 transform -rotate-45 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-32 left-32 w-20 h-20 border border-green-400/50 transform rotate-12 animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Main content */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="text-center space-y-16">
          {/* Logo with neon effect */}
          <div className="relative group flex justify-center">
            {/* Logo container */}
            <div className="relative">
              <img 
                src="/images/splash.png" 
                alt="ProfStudy Splash" 
                className="h-28 w-28 object-contain drop-shadow-2xl mx-auto"
              />
            </div>
          </div>

          {/* Title with neon text effect */}
          <div className="space-y-6">
            <h1 className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 tracking-tighter">
              ProfStudy
            </h1>
            <p className="text-2xl text-gray-300 font-light tracking-[0.3em] uppercase">
              Admin Panel
            </p>
          </div>

          {/* Cyberpunk loading bar */}
          <div className="w-96 mx-auto space-y-6">
            <div className="relative">
              {/* Background bar */}
              <div className="w-full h-3 bg-black/50 rounded-full overflow-hidden border border-cyan-400/30">
                {/* Progress bar */}
                <div 
                  className="h-full bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 rounded-full transition-all duration-300 ease-out shadow-lg shadow-cyan-400/50"
                  style={{ width: `${progress}%` }}
                />
                
                {/* Scanning line effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse" />
              </div>
              
              {/* Percentage display */}
              <div className="absolute -top-8 right-0 text-cyan-400 font-mono text-sm font-bold">
                {progress}%
              </div>
            </div>
            
            <p className="text-sm text-gray-400 font-mono tracking-wider">
              {progress < 100 ? 'SYSTEM_INITIALIZING...' : 'READY_TO_LAUNCH'}
            </p>
          </div>

          {/* Animated status indicators */}
          <div className="flex justify-center space-x-4">
            <div className="w-4 h-4 bg-cyan-400 rounded-full animate-ping shadow-lg shadow-cyan-400/50"></div>
            <div className="w-4 h-4 bg-purple-400 rounded-full animate-ping shadow-lg shadow-purple-400/50" style={{ animationDelay: '0.3s' }}></div>
            <div className="w-4 h-4 bg-pink-400 rounded-full animate-ping shadow-lg shadow-pink-400/50" style={{ animationDelay: '0.6s' }}></div>
          </div>
        </div>
      </div>

      {/* Scan lines effect */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-full h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent animate-pulse"
            style={{ 
              top: `${i * 5}%`,
              animationDelay: `${i * 0.1}s`
            }}
          />
        ))}
      </div>

      {/* Fade out overlay */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-600 ${
          progress >= 100 ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );
};

export default SplashScreen;
